import { useCallback, useRef } from 'react';

import { useMutation } from '@apollo/client';
import { PlusIcon } from '@clubspark-react/ui';
import { Grid } from '@mui/material';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

import EditableContactDetails from 'components/dialogs/playerProfileDialog/editableContactDetails/EditableContactDetails';
import { ContentTypeEnum } from 'components/dialogs/playerProfileDialog/editableContactDetails/utils';
import { SET_PERSON_TOURNAMENT_CONTACT_INFO } from 'config/graphql/queries';

import useStyles from './style';

type ParticipantContactDetailsProps = {
  contactData: {
    contactPhoneNumbers: string[];
    contactEmailAddresses: string[];
  };
  tournamentId: string;
  personId: string;
  updateContactDetails: (data: { contactPhoneNumbers: string[]; contactEmailAddresses: string[] }) => void;
};

const MAX_PHONE_NUMBERS = 3;

const ParticipantContactDetails = ({
  contactData,
  tournamentId,
  personId,
  updateContactDetails,
}: ParticipantContactDetailsProps) => {
  const { classes } = useStyles();

  const emailInputRef = useRef<{ handleClick: (e: React.MouseEvent<HTMLElement>) => void }>(null);
  const phoneInputRef = useRef<{ handleClick: (e: React.MouseEvent<HTMLElement>) => void }>(null);
  const { t } = useTranslation();
  const handleAddEmail = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (emailInputRef.current) {
        emailInputRef.current.handleClick(e);
      }
    },
    [emailInputRef],
  );
  const handleAddPhone = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (phoneInputRef.current) {
        phoneInputRef.current.handleClick(e);
      }
    },
    [phoneInputRef],
  );

  const [setPersonTournamentContactInfo] = useMutation(SET_PERSON_TOURNAMENT_CONTACT_INFO);

  const handleSubmit = useCallback(
    (type: ContentTypeEnum, value: string[]) => {
      const emails = type === ContentTypeEnum.EMAIL ? value : contactData?.contactEmailAddresses;
      const phones =
        type === ContentTypeEnum.PHONE
          ? value.map((num) => {
              try {
                return parsePhoneNumber(num)?.nationalNumber;
                // Some previously entered phone numbers may be invalid and require error handling.
                //eslint-disable-next-line unused-imports/no-unused-vars
              } catch (err) {
                return num;
              }
            })
          : contactData?.contactPhoneNumbers;
      setPersonTournamentContactInfo({ variables: { tournamentId, personId, emails, phones } });
      updateContactDetails({ contactPhoneNumbers: phones, contactEmailAddresses: emails });
    },
    [
      contactData?.contactEmailAddresses,
      contactData?.contactPhoneNumbers,
      personId,
      setPersonTournamentContactInfo,
      tournamentId,
      updateContactDetails,
    ],
  );

  return (
    <>
      <EditableContactDetails
        inputRef={emailInputRef}
        type={ContentTypeEnum.EMAIL}
        values={contactData?.contactEmailAddresses}
        onSubmit={handleSubmit}
      />
      <EditableContactDetails
        inputRef={phoneInputRef}
        type={ContentTypeEnum.PHONE}
        values={contactData?.contactPhoneNumbers}
        onSubmit={handleSubmit}
      />
      <Grid container gap={2.5}>
        {contactData?.contactEmailAddresses.length < MAX_PHONE_NUMBERS && (
          <button className={classes.button} onClick={handleAddEmail}>
            <PlusIcon />
            <span>{t('addEmail')}</span>
          </button>
        )}
        {contactData?.contactPhoneNumbers.length < MAX_PHONE_NUMBERS && (
          <button className={classes.button} onClick={handleAddPhone}>
            <PlusIcon />
            <span>{t('addPhone')}</span>
          </button>
        )}
      </Grid>
    </>
  );
};
export default ParticipantContactDetails;
